/**
 * Per-status row action set — Flutter parity with `appointment_status.dart`.
 *
 * Action ids reflect what should happen, not the current status:
 *   'mark-arrived'     → status update to 'Arrived'
 *   'mark-no-show'     → status update to 'NoShow'
 *   'mark-checked-in'  → status update to 'CheckedIn'
 *   'confirm'          → status update to 'Booked'      (from Pending)
 *   'unmark-arrived'   → status update to 'Booked'      (from Arrived)
 *   'unmark-checked-in'→ status update to 'Arrived'     (from CheckedIn)
 *   'unmark-no-show'   → status update to 'Booked'      (from NoShow)
 *   'reschedule'       → opens reschedule modal
 *   'cancel'           → opens cancel dialog
 *   'edit'             → opens edit panel
 *   'follow-up'        → opens create panel pre-filled
 *   'overflow'         → opens overflow menu
 *   'add-note'         → opens add-note dialog (from overflow menu)
 *   'open-billing-tab' → navigates to the appointment view panel with ?tab=billing
 *                        (PROD-915 — gated on Show_Billing_Tab + status not in
 *                        {Booked, Pending, NoShow}; mirrors Flutter
 *                        `calendar_tab.dart:2647-2659`)
 *   'view-recurring-series' → navigates to ?tab=appointments to show the
 *                        full recurring series for the row (PROD-916 —
 *                        gated on `row.isRecurring === true`; mirrors
 *                        Flutter `calendar_tab.dart:2662-2679`)
 *   'view-pvs'         → opens the Post-Visit Summary dialog (PROD-1651 —
 *                        gated on status Arrived / Fulfilled; mirrors Flutter
 *                        `calendar_tab.dart:2629-2640`)
 *
 * GRADUATED into Signal DS `8e43ff4` (packaged as 1.67.0-local overlay here) and
 * re-exported below. The package's union was widened from 15 to 17 members to add
 * 'view-pvs' and 'open-encounter' — the two ids that previously blocked PA from
 * consuming this type (measured 2026-09-21: PA's 17 vs the package's 15, everything
 * else byte-identical). Kept as a *type* re-export (not a value) because it is a
 * `type` declaration; `AppointmentAction`/`ActionTone` and the retained local
 * functions below (`getPopupActionsFor`, `getPanelActionsFor`, `action`) all key
 * off this same union.
 *
 * NOTE: the package's `AppointmentAction.icon` is typed against the DS's OWN
 * (narrower) `ScripsIconName` — an interim icon-font carrier ported 1:1 alongside
 * the lift, not PA's broader `@/shared/ui/components` union of the same name. The
 * local `action()` builder below is typed against `AppointmentAction['icon']`
 * rather than PA's `ScripsIconName` for exactly this reason; every literal this
 * file passes (`check`/`noShow`/`refresh`/`cancel`/`edit`/`checkedIn`/
 * `unmarkAsArrived`/`unmarkAsCheckedIn`/`appointment`) exists in both sets.
 */
export type { AppointmentActionId, ActionTone, AppointmentAction } from '@scripsteam/scrips-signal-ds';
import type { AppointmentActionId, ActionTone, AppointmentAction } from '@scripsteam/scrips-signal-ds';

interface ActionContext {
  /** Lower-cased status code from `appointment.status.code`. */
  statusCode: string;
  /** True if the appointment falls on today (gates many actions in Flutter). */
  isToday: boolean;
  /** True if the appointment's location.name is "Home" — hides No Show. */
  isHomeLocation: boolean;
  /** True if part of a recurring series — hides Follow-up. */
  isRecurring: boolean;
}

/**
 * Returns the ordered action set to render in the row, mirroring
 * `appointment_status.dart` exactly. Cancelled returns []; Fulfilled
 * returns [follow-up?, overflow]; everything else returns 3-5 entries.
 *
 * Caller renders these left-to-right and is responsible for wiring each
 * id to the appropriate hook (status update, cancel, reschedule, etc.).
 *
 * GRADUATED into Signal DS and re-exported. Byte-identical to PA's former local copy
 * (`diff`-clean, `ActionContext` matches the package's parameter shape field-for-field) —
 * the only thing blocking the re-export was the `AppointmentActionId` union width (17 vs
 * 15 members), fixed upstream in `8e43ff4`. Measured 2026-09-21/22.
 */
export { getRowActionsFor } from '@scripsteam/scrips-signal-ds';

/**
 * Popup-variant action set — Flutter parity with `appointment_status.dart`
 * (the bottom-of-popup action bar). Rules differ from the row variant:
 *
 *   - Status transitions (mark-arrived / mark-no-show / mark-checked-in /
 *     unmark-* / confirm) are gated by `isToday` (same as the row).
 *   - Reschedule is shown when status is Booked (NOT gated by isToday).
 *   - Cancel is shown unless status is terminal (NOT gated by isToday).
 *   - Follow-up appears when Fulfilled + not recurring.
 *
 * Edit is intentionally NOT included — the popup header pencil already
 * exposes it; the bottom row in Flutter doesn't duplicate.
 *
 * RELATIONSHIP TO `getRowActionsFor`:
 *   Both functions encode Flutter's status-transition rules. They share
 *   the same vocabulary (AppointmentAction shape, ScripsIcon names, the
 *   canonical ActionTone palette) but differ on which actions render
 *   under which conditions. Whenever the row variant changes (new
 *   status code, new transition, icon rename), AUDIT THIS FUNCTION FOR
 *   PARALLEL DRIFT and update both. A shared base helper is intentionally
 *   not extracted yet because the divergence rules (which actions are
 *   isToday-gated, which are status-gated) are clearer when expressed
 *   branch-by-branch in each variant. Consider extraction when a third
 *   variant ships (e.g. the full-screen edit view in PROD-377).
 */
export function getPopupActionsFor(ctx: ActionContext): AppointmentAction[] {
  const { statusCode, isToday, isHomeLocation, isRecurring } = ctx;
  const code = statusCode.toLowerCase();
  const out: AppointmentAction[] = [];

  // Status transitions (isToday-gated)
  if (isToday) {
    if (code === 'booked') {
      out.push(action('mark-arrived', 'check', 'Mark as Arrived', 'success'));
      if (!isHomeLocation) {
        out.push(action('mark-no-show', 'noShow', 'Mark as No Show', 'warning'));
      }
    }
    if (code === 'pending') {
      out.push(action('confirm', 'check', 'Confirm', 'success'));
    }
    if (code === 'arrived') {
      out.push(action('mark-checked-in', 'checkedIn', 'Mark as Checked In', 'success'));
      out.push(action('unmark-arrived', 'unmarkAsArrived', 'Unmark as Arrived', 'muted'));
    }
    if (code === 'checkedin' || code === 'checked-in') {
      out.push(action('unmark-checked-in', 'unmarkAsCheckedIn', 'Unmark as Checked In', 'muted'));
    }
    if (code === 'noshow' || code === 'no-show') {
      out.push(action('unmark-no-show', 'unmarkAsArrived', 'Unmark No Show', 'muted'));
    }
  }

  // Reschedule — Booked only, no isToday gate
  if (code === 'booked') {
    out.push(action('reschedule', 'refresh', 'Reschedule', 'info'));
  }

  // Cancel — unless terminal, no isToday gate
  if (code !== 'cancelled' && code !== 'canceled' && code !== 'noshow' && code !== 'no-show' && code !== 'fulfilled') {
    out.push(action('cancel', 'cancel', 'Cancel', 'error'));
  }

  // Follow-up — Fulfilled + non-recurring
  if (code === 'fulfilled' && !isRecurring) {
    out.push(action('follow-up', 'appointment', 'Follow-up Appointment', 'info'));
  }

  return out;
}

/**
 * Panel-variant action set — Flutter parity with the LEFT edit-panel status
 * menu in `create_appointment_widget.dart:2562-2707` (the vertical, full-width
 * text-button menu shown above "Cancel Appointment" on the view/edit
 * appointment screen — NOT the calendar row/popup icon bar).
 *
 * Differences from the row/popup variants:
 *   - Gated by the 3-day `isShowStatusButtons` window (see
 *     `status-button-window.ts`), NOT strict `isToday`.
 *   - Full-word labels: "Mark As Arrived" / "Mark As No Show" /
 *     "Mark As Checked In" / "Unmark As Arrived" / "Unmark As Checked In" /
 *     "Confirm Appointment" / "Unmark No Show" / "Cancel Appointment".
 *   - NO home-location gate on "Mark As No Show" (the icon bar has one; the
 *     panel's `button2TextByStatus` does not).
 *   - NO Reschedule / Follow-up / Edit — the panel menu is transitions +
 *     cancel/unmark only. Reschedule lives on the calendar popup.
 *   - Outer gate drops the menu entirely for Cancelled + Fulfilled.
 *   - All transition buttons render brand-blue (`enabledBtnBGColor`),
 *     Unmark No Show green (`successTabColor`), Cancel red.
 *
 * Structure mirrors the Dart exactly:
 *   Section A (window && status != NoShow):
 *     Pending → [Confirm Appointment]
 *     else    → [button1 (hidden when CheckedIn), button2]
 *   Section B:
 *     NoShow  → window ? [Unmark No Show] : []
 *     else    → [Cancel Appointment]
 */
export function getPanelActionsFor(ctx: {
  /** Lower-or-any-cased status code from `appointment.status.code`. */
  statusCode: string;
  /** `isShowStatusButtons(start)` — the 3-day window (NOT same-day). */
  inStatusWindow: boolean;
  /** Part of a recurring series — cancel routes through the scope dialog. */
  isRecurring: boolean;
}): AppointmentAction[] {
  const code = ctx.statusCode.toLowerCase();

  // Outer gate (Flutter :2562-2567): edit-only block hidden for terminal states.
  if (code === 'cancelled' || code === 'canceled' || code === 'fulfilled') {
    return [];
  }

  const out: AppointmentAction[] = [];
  const isNoShow = code === 'noshow' || code === 'no-show';

  // ── Section A — status transitions (window-gated, not for NoShow) ──────
  if (ctx.inStatusWindow && !isNoShow) {
    if (code === 'pending') {
      // Flutter :2650 — Pending shows a single "Confirm Appointment".
      out.push(action('confirm', 'check', 'Confirm Appointment', 'info'));
    } else {
      // button1 — hidden when CheckedIn (Flutter :2583-2586).
      if (code === 'booked' || code === 'proposed') {
        out.push(action('mark-arrived', 'check', 'Mark As Arrived', 'info'));
      } else if (code === 'arrived') {
        out.push(action('mark-checked-in', 'checkedIn', 'Mark As Checked In', 'info'));
      }
      // button2 (Flutter button2TextByStatus :2832-2845).
      if (code === 'booked' || code === 'proposed') {
        out.push(action('mark-no-show', 'noShow', 'Mark As No Show', 'info'));
      } else if (code === 'arrived') {
        out.push(action('unmark-arrived', 'unmarkAsArrived', 'Unmark As Arrived', 'info'));
      } else if (code === 'checkedin' || code === 'checked-in') {
        out.push(action('unmark-checked-in', 'unmarkAsCheckedIn', 'Unmark As Checked In', 'info'));
      }
    }
  }

  // ── Section B — Unmark No Show / Cancel (Flutter :2660-2701) ───────────
  if (isNoShow) {
    // NoShow row shows "Unmark No Show" ONLY (green), and ONLY inside the
    // window; out of window it shows nothing. Cancel is INTENTIONALLY absent
    // for NoShow — Flutter's ternary (:2688-2701) returns Container() for a
    // NoShow that isn't unmarkable, never the Cancel button. Do NOT add Cancel
    // here to "fix" the asymmetry; it's a deliberate Flutter behaviour.
    if (ctx.inStatusWindow) {
      out.push(action('unmark-no-show', 'unmarkAsArrived', 'Unmark No Show', 'success'));
    }
  } else {
    out.push(action('cancel', 'cancel', 'Cancel Appointment', 'error'));
  }

  return out;
}

/**
 * Resolve an action id → the status code it should write. Returns null
 * for non-status actions (reschedule / cancel / edit / follow-up / overflow / add-note).
 *
 * GRADUATED into Signal DS and re-exported. Byte-identical to PA's former local copy —
 * blocked only by the `AppointmentActionId` union width (17 vs 15 members), fixed
 * upstream in `8e43ff4`. Measured 2026-09-21/22.
 */
export { statusCodeForAction } from '@scripsteam/scrips-signal-ds';

// ─── Overflow-menu visibility predicates (PROD-914/915/916) ──────────────
//
// Pure boolean predicates that mirror Flutter `calendar_tab.dart:2566-2748`
// for the per-row overflow menu items. Pulled out as exports so the
// component can stay slim and the tests can exercise the rules directly.
//
// "Cancel appointment" already had its terminal-status gate inside the
// component (cancel hides on cancelled / noshow / fulfilled). The 3 new
// rules below add to the menu, they don't replace the cancel rule.

/**
 * Status codes that HIDE the "Open 'Billing' Tab" menu item. Flutter
 * `calendar_tab.dart:2641-2646` excludes Booked / Pending / NoShow — the
 * billing tab is meaningless before the appointment is confirmed (Booked
 * = unconfirmed-pending in Flutter terminology, NoShow = patient never
 * arrived). All other statuses get the menu item.
 *
 * Distinct from the view-appointment panel's `TERMINAL_BILLING_STATUSES`
 * which ALSO excludes Cancelled (that's the tab-visibility rule on the
 * panel itself). We match Flutter's menu rule verbatim; the panel
 * gracefully falls back to the Patient tab when ?tab=billing arrives
 * with the billing tab hidden.
 */
// (the set itself now lives in the package alongside the predicate that reads it)

/**
 * Returns true when "Open 'Billing' Tab" should appear on the row
 * overflow menu — PROD-915 / Flutter `calendar_tab.dart:2641-2646`.
 *
 * Caller is responsible for the `Show_Billing_Tab` permission check
 * (a hook, can't be a pure helper).
 */
// GRADUATED into Signal DS 1.45.0 and re-exported. Identical predicate, and the package's
// BILLING_MENU_HIDDEN_STATUSES set is identical too (booked/pending/noshow/no-show). It takes a
// plain string, so it never depended on the AppointmentActionId union width that, until
// `8e43ff4`, kept getRowActionsFor/statusCodeForAction local (see those functions above).
export { shouldShowBillingTabMenuItem } from '@scripsteam/scrips-signal-ds';

/**
 * Returns true when "View Recurring Series" should appear on the row
 * overflow menu — PROD-916 / Flutter `calendar_tab.dart:2661-2680`.
 * Single-purpose helper for symmetry with the billing-tab predicate.
 */
// GRADUATED into Signal DS 1.45.0 and re-exported. Takes a boolean, so it is union-independent.
export { shouldShowViewRecurringSeriesMenuItem } from '@scripsteam/scrips-signal-ds';

/**
 * Returns true when "View PVS" (Post-Visit Summary) should appear on the row
 * overflow menu — PROD-1651 / Flutter `calendar_tab.dart:2629-2640`. Shown when
 * the appointment status is Arrived or Fulfilled (the PVS only exists once the
 * patient has been seen). Same gate as the popup's PVS footer button (PROD-875).
 *
 * GRADUATED into Signal DS `8e43ff4` and re-exported. Identical predicate; this was
 * one of the two PA-only ids that widened the package's AppointmentActionId union.
 */
export { shouldShowPvsMenuItem } from '@scripsteam/scrips-signal-ds';

/**
 * Returns true when "Open Encounter" should appear on the row overflow menu.
 *
 * Flutter `calendar_tab.dart:2596-2603`: shown when the status is Arrived,
 * Checked-in or Fulfilled — you cannot open an encounter for a Booked,
 * Pending, Cancelled or No-Show appointment — AND ONLY on the provider app
 * (`currentAppType == AppType.PA`). That app-type guard is why PM has no such
 * item and why this is a deliberate PA-over-PM difference, not drift.
 *
 * Same status set as the panel footer's "Open encounter" (PROD-1190).
 *
 * GRADUATED into Signal DS `8e43ff4` and re-exported. Identical predicate; this was
 * the other PA-only id that widened the package's AppointmentActionId union.
 */
export { shouldShowOpenEncounterMenuItem } from '@scripsteam/scrips-signal-ds';

// ─── Internal builders ───────────────────────────────────────────────────

function action(
  id: AppointmentActionId,
  icon: AppointmentAction['icon'],
  label: string,
  tone: ActionTone,
): AppointmentAction {
  return { id, icon, label, tone };
}
